// ---------------------------------------------------------------------------
// Design Tokens — centralized visual constants for the ActionFlow Editor.
//
// Matches Page Editor ContextToolbar / AddElementDropdown patterns.
// Single source of truth: change once, applies everywhere.
// ---------------------------------------------------------------------------

// ---- Icon Sizes (Tailwind className pattern — Page Editor parity) ----

export const ICON = {
  /** Standard toolbar/menu icon — matches Page Editor h-3.5 w-3.5 (14px) */
  sm: 'h-3.5 w-3.5',
  /** Toolbar top-level icon — 16px */
  md: 16,
  /** Empty state / large display icon */
  lg: 24,
} as const

// ---- Badge (node center icon circle) ----

export const BADGE = {
  /** Single-output node badge diameter */
  singleSize: 48,
  /** Single-output node icon size (inside badge) */
  singleIcon: 28,
  /** Multi-output node badge diameter (semi-transparent watermark) */
  multiSize: 36,
  /** Multi-output node icon size (inside badge) */
  multiIcon: 20,
} as const

// ---- Port (connection handle dots) ----

export const PORT = {
  /** Port dot diameter */
  dotSize: 12,
  /** Input port Tailwind classes */
  inputClass: '!w-3 !h-3 !bg-gray-400 !border-2 !border-background',
  /** Output port Handle Tailwind classes (transparent, just a hit target) */
  outputHandleClass: '!w-3 !h-3 !border-0 !bg-transparent',
  /** Multi-port Y distribution — fraction of height for first/last port */
  yStart: 0.25,
  yRange: 0.5,
  /** Port label styles */
  labelClass: 'absolute text-[9px] text-muted-foreground text-right truncate leading-none right-2',
  labelMaxWidth: 50,
} as const

// ---- Plus Button (add node from port) ----

export const PLUS_BUTTON = {
  /** Connecting line width (Tailwind) */
  lineClass: 'w-5 h-0.5 bg-gray-400',
  /** Circle button classes */
  circleClass: 'w-6 h-6 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-600 flex items-center justify-center cursor-crosshair shadow-sm',
  /** Plus icon size */
  iconSize: 14,
  /** Position offset from Handle (accounting for translate(50%,-50%) transform) */
  offsetTop: -6,
  offsetLeft: 12,
} as const

// ---- Quick Action Bar (node hover toolbar — Page Editor ContextToolbar parity) ----

export const QUICK_BAR = {
  /** Container classes — matches Page Editor ContextToolbar */
  containerClass: 'absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-popover border rounded-xl shadow-md px-1 py-0.5 z-10 whitespace-nowrap',
  /** Standard button — matches Page Editor ToolbarButton h-7 w-7 */
  buttonClass: 'flex h-7 w-7 items-center justify-center rounded text-foreground/70 hover:bg-accent hover:text-foreground transition-colors',
  /** Destructive button variant */
  deleteButtonClass: 'flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-destructive/10 transition-colors',
  /** Separator */
  separatorClass: 'mx-0.5 h-4 w-px bg-border',
} as const

// ---- Quick Action Popup (add node menu — Page Editor AddElementDropdown parity) ----

export const QUICK_POPUP = {
  /** Container classes — matches Page Editor AddElementDropdown */
  containerClass: 'absolute z-30 bg-popover border rounded-xl shadow-lg p-1 w-40',
  /** Header text */
  headerClass: 'px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider',
  /** Menu item button */
  itemClass: 'flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors',
  /** Menu item icon */
  itemIconClass: 'h-3.5 w-3.5 text-muted-foreground',
  /** Footer separator */
  footerSeparatorClass: 'border-t mt-1 pt-1',
  /** Footer item */
  footerItemClass: 'flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors',
  /** Left offset from node card */
  leftOffset: 44,
} as const

// ---- Edge (connection line) ----

export const EDGE = {
  /** Normal stroke width */
  strokeWidth: 2,
  /** Selected stroke width */
  selectedStrokeWidth: 3,
  /** Invisible hit area stroke width */
  hitAreaStrokeWidth: 20,
  /** Selected glow filter */
  selectedFilter: 'drop-shadow(0 0 3px rgba(0,0,0,0.15))',
  /** Delete button — matches Page Editor destructive button pattern */
  deleteButtonClass: 'nodrag nopan flex items-center justify-center h-7 w-7 rounded-xl bg-foreground text-background hover:bg-destructive hover:text-destructive-foreground shadow-md transition-colors',
} as const

// ---- Node Card ----

export const NODE_CARD = {
  /** Trigger node border radius (stadium: pill-left, rounded-right) */
  triggerRadius: '48px 12px 12px 48px',
  /** External label area height */
  labelHeight: 36,
  /** External label overflow width (extends beyond card) */
  labelOverflow: 20,
  /** Node name label */
  nameLabelClass: 'text-xs font-medium text-foreground truncate leading-tight',
  /** Node subtitle (action type label) */
  subtitleClass: 'text-[10px] text-muted-foreground truncate leading-tight',
  /** Execution status dot */
  statusDotClass: 'absolute top-1 right-1 w-3 h-3 rounded-full border-2 border-background',
} as const

// ---- Canvas (React Flow settings) ----

export const CANVAS = {
  /** Background dot gap */
  bgGap: 20,
  /** Background dot size */
  bgSize: 1,
  /** Background dot color */
  bgColor: 'hsl(0 0% 89.8%)',
  /** Snap grid */
  snapGrid: [10, 10] as [number, number],
  /** Fit view padding */
  fitViewPadding: 0.3,
  /** Fit view max zoom */
  fitViewMaxZoom: 1.2,
} as const

// ---- MiniMap ----

export const MINIMAP = {
  width: 120,
  height: 80,
  /**
   * Node type → color mapping.
   *
   * Theme-aware via CSS variables (cogo-styles base+inline tokens added
   * Sub-project C PR0). Mapping mirrors `FLOW_NODE_COLORS` (concept2 § 5
   * Events) at apps/cogo-studio/src/shell/ide-v2/surfaces/flow-node-colors.ts:
   *
   *   trigger     → nav     (BLUE  — flow entry/event trigger)
   *   conditional → control (AMBER — branch / switch / loop)
   *   callback    → data    (MINT  — async result resolution)
   *   default     → meta    (INK_700 foreground — fallback)
   *
   * SVG `fill` attribute resolves CSS vars at render time, so React Flow's
   * MiniMap nodeColor callback can return these directly.
   */
  nodeColors: {
    trigger: 'var(--color-blue)',
    conditional: 'var(--color-amber)',
    callback: 'var(--color-mint)',
    default: 'var(--color-foreground)',
  },
} as const

// ---- Auto Layout ----

export const LAYOUT = {
  /** Horizontal gap between columns */
  hGap: 120,
  /** Vertical gap between rows */
  vGap: 80,
  /** Starting position */
  startX: 100,
  startY: 100,
  /** Palette insertion gap (added to NODE_WIDTH) */
  insertionGap: 120,
  /** Multi-output Y offset per port */
  portYOffset: 80,
} as const

// ---- Palette (left panel) ----

export const PALETTE = {
  /** Section header */
  headerClass: 'px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground',
  /** Category button */
  categoryClass: 'w-full flex items-center gap-2 px-2 py-1.5 text-[10px] tracking-widest uppercase font-semibold rounded hover:bg-accent transition-colors',
  /** Action item button */
  itemClass: 'w-full flex items-center gap-2 px-2 py-1 text-[10px] uppercase font-semibold tracking-widest rounded hover:bg-accent transition-colors',
  /** Trigger button */
  triggerClass: 'w-full flex items-center gap-2 px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded hover:bg-accent transition-colors mb-2',
  /** Helper/badge text (counts, descriptions) */
  helperClass: 'text-[10px] text-muted-foreground',
} as const

// ---- Inspector (right panel) ----

export const INSPECTOR = {
  /** Node header badge */
  badgeSize: 'w-8 h-8',
  badgeClass: 'w-8 h-8 rounded-xl flex items-center justify-center',
  /** Section header */
  sectionHeaderClass: 'text-[10px] font-semibold uppercase tracking-widest text-muted-foreground',
  /** Info row */
  infoRowClass: 'text-[11px] text-muted-foreground space-y-1 border-b border-foreground/10 pb-2',
  /** Node label input */
  labelInputClass: 'w-full text-sm font-medium bg-transparent border-b border-transparent hover:border-border focus:border-primary outline-none px-0.5 py-0.5',
  /** Subtitle text */
  subtitleClass: 'text-[10px] text-muted-foreground',
} as const

// ---- Toolbar (top bar — shared with Page Editor) ----

export const TOOLBAR = {
  /** Container — matches Page Editor EditorToolbar */
  containerClass: 'flex items-center justify-between px-3 h-10 border-b bg-background',
  /** Button (same as Page Editor) */
  buttonClass: 'flex h-7 w-7 items-center justify-center rounded-xl transition-colors text-muted-foreground hover:bg-accent hover:text-foreground',
  /** Separator — matches Page Editor */
  separatorClass: 'w-px h-4 bg-border mx-1',
  /** Icon size (lucide size prop) */
  iconSize: 16,
} as const

// ---- Forms (inspector parameter forms) ----

export const FORM = {
  /** Form field label — matches inspector section header style */
  labelClass: 'text-[10px] text-muted-foreground uppercase',
  /** Hint / description text */
  hintClass: 'text-[10px] text-muted-foreground',
  /** Empty state / italic hint */
  emptyClass: 'text-[10px] text-muted-foreground italic',
  /** Small action icon size (Plus, X buttons in forms) */
  actionIconSize: 12,
} as const

// ---- Status Bar ----

export const STATUS_BAR = {
  containerClass: 'h-6 px-3 flex items-center gap-3 border-t bg-background text-xs text-muted-foreground shrink-0',
} as const
